import { Link, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, X } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1624880056139-d1212d7ff347?w=1200&q=80";

const FIELDS = [
  { key: "price_range_eur", label: "Prezzo" },
  { key: "category", label: "Categoria" },
  { key: "fuel_type", label: "Alimentazione" },
  { key: "transmission", label: "Cambio" },
  { key: "horsepower", label: "Potenza" },
  { key: "year_range", label: "Anni" },
];

export default function Compare() {
  const location = useLocation();
  const cars = location.state?.cars;

  if (!cars || cars.length !== 2) {
    return <Navigate to="/" replace />;
  }

  const back = location.state?.resultId ? `/results/${location.state.resultId}` : "/";

  return (
    <div className="min-h-screen" data-testid="compare-page">
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="font-heading text-xl font-semibold tracking-tight">
          auto<span className="text-[#0055FF]">.</span>advisor
        </Link>
        <Link to={back} className="inline-flex items-center gap-2 text-sm font-medium text-[#111] hover:text-[#0055FF]" data-testid="compare-back-link">
          <ArrowLeft className="w-4 h-4" /> Torna ai risultati
        </Link>
      </header>

      <main className="max-w-6xl mx-auto px-6 pt-4 pb-24">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          <span className="text-xs uppercase tracking-widest text-[#0055FF] font-medium">Confronto</span>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-semibold tracking-tight">
            Confronto affiancato
          </h1>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {cars.map((car, i) => (
            <motion.div
              key={`${car.brand}-${car.model}-${i}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="rounded-3xl border border-[#EAEAEA] bg-white overflow-hidden"
              data-testid={`compare-car-${i}`}
            >
              <div className="aspect-[16/9] bg-[#F2F4F8] flex items-center justify-center">
                <img
                  src={car.image_url || FALLBACK}
                  onError={(e) => (e.currentTarget.src = FALLBACK)}
                  alt={`${car.brand} ${car.model}`}
                  className="w-full h-full object-contain p-4"
                />
              </div>
              <div className="p-6">
                <p className="text-sm text-[#666]">{car.brand}</p>
                <h2 className="font-heading text-2xl font-semibold tracking-tight">{car.model}</h2>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 rounded-3xl border border-[#EAEAEA] bg-white overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {FIELDS.map((f, idx) => (
                <tr key={f.key} className={idx % 2 ? "bg-[#F9F9FB]" : ""}>
                  <td className="px-6 py-4 font-medium text-[#666] w-40">{f.label}</td>
                  {cars.map((c, i) => (
                    <td key={i} className="px-6 py-4 text-[#111] capitalize" data-testid={`compare-${f.key}-${i}`}>
                      {c[f.key] ? String(c[f.key]).replace(/_/g, " ") : "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {cars.map((car, i) => (
            <div key={`bottom-${car.brand}-${car.model}-${i}`} className="rounded-3xl border border-[#EAEAEA] bg-white p-6">
              <h3 className="font-heading text-lg font-medium mb-4">{car.brand} {car.model}</h3>
              <p className="text-sm text-[#333] leading-relaxed mb-4">{car.why_recommended}</p>
              <div className="grid grid-cols-1 gap-3">
                {car.pros?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#666] font-medium mb-2">Pro</p>
                    <ul className="space-y-1">
                      {car.pros.map((p, k) => (
                        <li key={k} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 mt-0.5 text-[#0055FF] shrink-0" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {car.cons?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#666] font-medium mb-2">Contro</p>
                    <ul className="space-y-1">
                      {car.cons.map((p, k) => (
                        <li key={k} className="flex items-start gap-2 text-sm">
                          <X className="w-4 h-4 mt-0.5 text-[#666] shrink-0" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
